import React, { useState } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;

const post = async (path, body) => {
  const res = await fetch(`${API}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`Request failed (${res.status})`);
  return res.json();
};

const Overlay = ({ onClose, children, testId }) => (
  <div className="mkt-modal-overlay" data-testid={`${testId}-overlay`} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
    <div className="mkt-modal" role="dialog" aria-modal="true" data-testid={testId}>
      <button type="button" className="mkt-modal-x" aria-label="Close" data-testid={`${testId}-close`} onClick={onClose}>✕</button>
      {children}
    </div>
  </div>
);

const INTERESTS = ['Investment', 'Grant partnership', 'Pilot facility', 'College / training provider', 'Employer partner', 'Other'];

export const PilotModal = ({ onClose }) => {
  const [form, setForm] = useState({ name: '', email: '', organization: '', interest: INTERESTS[0], message: '' });
  const [status, setStatus] = useState('idle'); // idle | sending | done | error
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.email.includes('@')) {
      setStatus('invalid');
      return;
    }
    setStatus('sending');
    try {
      await post('/api/leads', form);
      setStatus('done');
    } catch (_err) {
      setStatus('error');
    }
  };

  return (
    <Overlay onClose={onClose} testId="pilot-modal">
      {status === 'done' ? (
        <div className="mkt-modal-done" data-testid="pilot-modal-success">
          <div className="mkt-modal-done-ic">✓</div>
          <h3>Request received</h3>
          <p>Thanks, {form.name.split(' ')[0]} — we'll reach out within one business day to set up a conversation.</p>
          <button type="button" className="btn-or" onClick={onClose} data-testid="pilot-modal-done-btn">Done</button>
        </div>
      ) : (
        <form onSubmit={submit} noValidate>
          <span className="mkt-modal-tag">Schedule a Call</span>
          <h3 className="mkt-modal-h">Tell us where you're coming from.</h3>
          <p className="mkt-modal-p">Two minutes now saves the email back-and-forth. We'll follow up within one business day.</p>
          <label className="mkt-field"><span>Name *</span>
            <input value={form.name} onChange={set('name')} required placeholder="Jane Rivera" data-testid="pilot-input-name" />
          </label>
          <label className="mkt-field"><span>Email *</span>
            <input type="email" value={form.email} onChange={set('email')} required placeholder="jane@fund.com" data-testid="pilot-input-email" />
          </label>
          <label className="mkt-field"><span>Organization</span>
            <input value={form.organization} onChange={set('organization')} placeholder="Rivera Capital / County DOC / College" data-testid="pilot-input-org" />
          </label>
          <label className="mkt-field"><span>I'm interested in</span>
            <select value={form.interest} onChange={set('interest')} data-testid="pilot-input-interest">
              {INTERESTS.map((i) => <option key={i} value={i}>{i}</option>)}
            </select>
          </label>
          <label className="mkt-field"><span>Anything specific? (optional)</span>
            <textarea rows={3} value={form.message} onChange={set('message')} placeholder="Timeline, check size, facility count…" data-testid="pilot-input-message" />
          </label>
          {status === 'invalid' && <div className="mkt-modal-err" data-testid="pilot-modal-invalid">Please enter your name and a valid email.</div>}
          {status === 'error' && <div className="mkt-modal-err" data-testid="pilot-modal-error">Something went wrong — please try again.</div>}
          <button type="submit" className="btn-or mkt-modal-submit" disabled={status === 'sending'} data-track="pilot-submit" data-testid="pilot-submit-btn">
            {status === 'sending' ? 'Sending…' : 'Request a Call →'}
          </button>
        </form>
      )}
    </Overlay>
  );
};

export const BriefModal = ({ onClose }) => {
  const [form, setForm] = useState({ name: '', email: '' });
  const [status, setStatus] = useState('idle');
  const [emailSent, setEmailSent] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email.trim() || !form.email.includes('@')) {
      setStatus('invalid');
      return;
    }
    setStatus('sending');
    try {
      const res = await post('/api/brief-request', form);
      setEmailSent(!!res.email_sent);
      const a = document.createElement('a');
      a.href = '/assets/AssistivOS-Investment-Brief.pdf';
      a.download = 'AssistivOS-Investment-Brief.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setStatus('done');
    } catch (_err) {
      setStatus('error');
    }
  };

  return (
    <Overlay onClose={onClose} testId="brief-modal">
      {status === 'done' ? (
        <div className="mkt-modal-done" data-testid="brief-modal-success">
          <div className="mkt-modal-done-ic">↓</div>
          <h3>Your brief is downloading</h3>
          <p>{emailSent
            ? 'A copy with the attachment is also on its way to your inbox.'
            : "We've saved your details — we'll follow up with the full materials shortly."}</p>
          <button type="button" className="btn-or" onClick={onClose} data-testid="brief-modal-done-btn">Done</button>
        </div>
      ) : (
        <form onSubmit={submit} noValidate>
          <span className="mkt-modal-tag">Investment Brief</span>
          <h3 className="mkt-modal-h">Get the one-page brief.</h3>
          <p className="mkt-modal-p">The download starts instantly — we'll also email you a copy so it's easy to share.</p>
          <label className="mkt-field"><span>Name</span>
            <input value={form.name} onChange={set('name')} placeholder="Jane Rivera" data-testid="brief-input-name" />
          </label>
          <label className="mkt-field"><span>Email *</span>
            <input type="email" value={form.email} onChange={set('email')} required placeholder="jane@fund.com" data-testid="brief-input-email" />
          </label>
          {status === 'invalid' && <div className="mkt-modal-err" data-testid="brief-modal-invalid">Please enter a valid email address.</div>}
          {status === 'error' && <div className="mkt-modal-err" data-testid="brief-modal-error">Something went wrong — please try again.</div>}
          <button type="submit" className="btn-or mkt-modal-submit" disabled={status === 'sending'} data-track="brief-submit" data-testid="brief-submit-btn">
            {status === 'sending' ? 'Preparing…' : 'Download the Brief ↓'}
          </button>
        </form>
      )}
    </Overlay>
  );
};
